import { ProductTypeT } from "src/pages/product/product-types";
import { ContractStep } from "../contract/contract-types";
import { CommonDateNUser, Order, Select } from "../public-types";

enum BillPayStep {
  TEMP_SAVE = "TEMP_SAVE",
  PAYMENT_READY = "PAYMENT_READY",
  PAYMENT_SUCCESS = "PAYMENT_SUCCESS",

  PAYMENT_FREE = "PAYMENT_FREE",
  PAYMENT_STOP = "PAYMENT_STOP",
  PAYMENT_DELAY = "PAYMENT_DELAY",
}

const searchStatusOption: Select[] = [
  { label: "전체", value: "", color: "" },
  { label: "저장", value: BillPayStep.TEMP_SAVE, color: "chip gray" },
  { label: "대기", value: BillPayStep.PAYMENT_READY, color: "status E" },
  { label: "성공", value: BillPayStep.PAYMENT_SUCCESS, color: "chip green" },
  { label: "중지", value: BillPayStep.PAYMENT_STOP, color: "chip gray" },
  { label: "무료", value: BillPayStep.PAYMENT_FREE, color: "chip blue" },
  { label: "연체", value: BillPayStep.PAYMENT_DELAY, color: "status G" },
];

enum ApplicationName {
  //빌링 스케줄 id (목록 응답의 content[].scheduleId)
  SCHEDULE_ID = "SCHEDULE_ID",
  //신청/계약(계약) id
  CONTRACT_ID = "CONTRACT_ID",
  //공간상품 id
  SPACE_PRODUCT_ID = "SPACE_PRODUCT_ID",
  //공간상품명
  SPACE_PRODUCT_NAME = "SPACE_PRODUCT_NAME",
  //신청/계약자명
  APPLICANT_NAME = "APPLICANT_NAME",
  //신청/계약자 법인명/상호
  APPLICANT_CORPORATION = "APPLICANT_CORPORATION",
  CONTRACT_APPLY_NUMBER = "CONTRACT_APPLY_NUMBER",
  CONTRACT_NUMBER = "CONTRACT_NUMBER",
  MB_ORGANIZATION_ID = "MB_ORGANIZATION_ID",
}
// 신청 번호 검색옵션은 계약 ID로 대체했다.
// 기존 "신청/계약 id" 옵션이 이미 CONTRACT_ID 라서, 같은 값의 옵션을 하나 더 두지 않고 라벨만 "계약 ID"로 바꿨다.
const searchTypeOption: Select[] = [
  { label: "전체", value: "" },
  // TODO(BE): 코드값 SCHEDULE_ID 는 예상값이다. RCB10 의 searchType 확정 enum 과 대조할 것.
  //           미배포 상태에서 선택하면 조회 결과가 비거나 오류가 날 수 있다.
  { label: "빌링 스케줄 ID", value: ApplicationName.SCHEDULE_ID },
  { label: "계약 ID", value: ApplicationName.CONTRACT_ID },
  { label: "상품명", value: ApplicationName.SPACE_PRODUCT_NAME },
  // { label: "상품id", value: ApplicationName.SPACE_PRODUCT_ID },
  // { label: "신청/계약자명", value: ApplicationName.APPLICANT_NAME },
  // { label: "법인/사업자 id", value: ApplicationName.MB_ORGANIZATION_ID },
  // { label: "계약 번호", value: ApplicationName.CONTRACT_NUMBER },
];

// 청구 종류(generationSource) 정의. 컬럼 라벨, 헤더 툴팁, 조건검색 필터 옵션의 단일 출처이며 나열 순서도 화면 순서다.
// 상수명이 API 요청 파라미터 generationSources 와 구분되도록 List 접미사를 쓴다.
// 코드값의 의미(정기/견적서/수동 청구)와 화면 라벨이 다르므로 API 문서 한글명을 그대로 쓰지 않는다.
// OVERDUE(연체 생성)는 노출 대상에서 제외되어 여기에 없다. 목록에서는 "-" 로 표기되고 필터로도 조회되지 않는다.
const generationSourceList: Array<{ value: GenerationSource; label: string; tooltip: string }> = [
  {
    value: "SCHEDULE",
    label: "이용료",
    tooltip: "공간임대 계약에 따라 발생하는 청구로, 설정된 이용료가 매월 정기 청구일에 청구됩니다.",
  },
  {
    value: "FACILITY",
    label: "공용공간 후불정산",
    tooltip: "계약에 포함된 충전금보다 공용공간을 더 사용한 경우 발생하는 청구로, 초과 사용 금액이 다음 달 5일에 청구됩니다.",
  },
  {
    value: "MANUAL",
    label: "관리자 수동 등록",
    tooltip: "관리자가 직접 등록한 청구로, 관리자가 입력한 금액이 지정한 청구일에 청구됩니다.",
  },
  {
    value: "QUOTATION",
    label: "공간예약 상품",
    tooltip: "공간예약 상품을 결제하면 발생하는 청구로, 견적서 기준 요금이 견적서 기준 날짜에 청구됩니다.",
  },
];

// 청구 종류 컬럼값 라벨 조회용
const generationSourceLabel: Record<string, string> = Object.fromEntries(generationSourceList.map(({ value, label }) => [value, label]));

// 조건검색의 청구 종류 필터 옵션. 빈 값(전체)이면 generationSources 를 전송하지 않는 것과 동일하게 전체 조회된다.
const generationSourceOption: Select[] = [{ label: "전체", value: "" }, ...generationSourceList.map(({ value, label }) => ({ label, value }))];

//목록 검색
interface SearchParams {
  id?: number;
  billPayStatus?: string;
  // 청구 종류 필터. API 는 복수 선택(구분자 ",")을 지원하지만 화면은 단일 선택 드롭다운이라 값 1개만 전송한다.
  generationSources?: string;
  searchType?: string;
  searchValue?: string;
  //검색일(from) YYYY-MM-DD
  searchStartTime?: string;
  //검색일(to) YYYY-MM-DD
  searchEndTime?: string;
  partnerName?: string;
  partnerId?: number;
  productTypes?: ProductTypeT[];
  isOverdue?: string | boolean;
  buildingId?: number;
  mbOrganizationId?: string;
  contractSteps?: string[];
  page?: number;
  size?: number;
  sort?: {
    orders: Array<Order>;
  };
}

// API 요청용 파라미터 (배열을 쉼표 구분 문자열로 변환)
export interface BillingScheduleApiParams extends Omit<SearchParams, "productTypes" | "contractSteps"> {
  productTypes?: string; // 배열을 쉼표로 join한 문자열
  contractSteps?: string; // 배열을 쉼표로 join한 문자열
}

// URL 쿼리 파라미터용 (클라이언트 라우팅)
export interface BillingScheduleUrlParams extends SearchParams {
  buildingName?: string;
  orgName?: string;
}

export interface BillingDetailWrap {
  scheduleList?: Array<BillingDetail>;
}

export interface BillingScheduleList extends CommonDateNUser {
  scheduleId?: string;
  contractId?: string;
  contractNumber?: string;
  contractManageId?: string;
  contractApplyNumber?: string;
  contractName?: string;
  spaceProductId?: string;
  spaceProductName?: string;
  mbOrganizationId?: string;
  payRegularDay?: number;
  frequency: "MONTHLY";
  contractStartTime?: string;
  contractEndTime?: string;
  contractStep?: ContractStep;
  isMntContain?: boolean;
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  billDueDate?: string;
  billAmount?: string;
  billPayStatus?: string;
  // 해당 행에 출력된 직전 청구서의 생성출처. 직전 청구서가 없으면 비어있다.
  generationSource?: GenerationSource;
  partnerId?: number;
  createdDate?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;
  contractorName?: string;
  /** @deprecated */
  applicantName?: string;
}

export interface BillingDetail extends CommonDateNUser {
  scheduleId?: string | number;
  contractId?: string;
  providerId?: string;
  supplyType?: string;
  contractPayType?: string;
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  regularPgcode?: string;
  payRegularMonth?: string;
  payRegularDay?: number;
  payDueDays?: number;
  taxInvoiceIssueMonth?: string;
  taxInvoiceIssueDay?: number;
  isUseTaxInvoice?: boolean; // 세금계산서 사용 여부
  isAutoTaxInvoice?: boolean; // 세금계산서 자동발행 여부
  cardInfo?: null | string;
  payInfo?: null | string;
  billkey?: null | string;
  accountInName?: string;
  overdueChargeStandard?: string;
  isAutoBilling?: boolean;
  isAutoSend?: boolean;
  isContractCancel?: boolean;
  contractCancelDate?: null | string;
  contractCancelStartOrder?: number;
  contractCancelReason?: string;
  isDeleted?: boolean;
  scheduleOverdueList?: Array<OverdueFeeDetail>;
  scheduleMessageList?: Array<ScheduleNoti>; // 공문/알림톡 목록
  [key: string]: any;
}

export interface ScheduleNoti extends CommonDateNUser {
  messageId?: string | number;
  messageType?: string;
  messageMedia?: string;
  messageLabel?: string;
  isSend?: boolean;
  isDeleted?: boolean;
  cmdType?: string;
  [key: string]: any;
}

export interface OverdueFeeDetail extends CommonDateNUser {
  overdueChargeStandard: string;
  overdueDays: number;
  overdueId: string | number;
  overdueRates: string | number;
}

/** 공급유형 (RENTAL: 임대, MNT: 관리) */
export type SupplyType = "RENTAL" | "MNT";

/** 결제방법 코드 (creditcard: 신용카드, banktransfer: 계좌이체, virtualaccount: 가상계좌, physicalcash: (물리적) 현금, other: 기타) */
export type PgCode = "creditcard" | "banktransfer" | "virtualaccount" | "physicalcash" | "other";

/** 세금계산서 발행목적 (BILLING: 청구, RECEIPT: 영수) */
export type TaxPurposeType = "BILLING" | "RECEIPT";

/** 청구결제상태 (PAYMENT_READY: 결제대기, PAYMENT_SUCCESS: 결제성공, PAYMENT_STOP: 결제중지) */
export type BillPayStatus = "PAYMENT_READY" | "PAYMENT_SUCCESS" | "PAYMENT_STOP";

/** 생성출처 (SCHEDULE: 스케줄 생성, QUOTATION: 견적서 생성, FACILITY: 공용시설 마감, OVERDUE: 연체 생성, MANUAL: 매뉴얼 생성) */
export type GenerationSource = "SCHEDULE" | "QUOTATION" | "FACILITY" | "OVERDUE" | "MANUAL";

/** 청구 상세 리스트 (품목별) */
export type CtBillDetailType = {
  dtlId: string; // 청구상세 id
  billId: string; // 청구 id
  dtlOrder: number; // 상세순번
  trDateTime: string; // 거래일시 (READ_ONLY)
  itemName: string; // 품목명
  baseAmount: number; // 기준가
  adjustAmount: number; // 조정가
  totalAmount: number; // 합계액
  supplyAmount: number; // 공급가
  taxAmount: number; // 부가세
  description: string; // 비고
  isDeleted: boolean; // 삭제여부
  createdDate: string; // 등록일자 (READ_ONLY)
  createdBy: string; // 등록자ID (READ_ONLY)
  modifiedDate: string; // 수정일자 (READ_ONLY)
  modifiedBy: string; // 수정자ID (READ_ONLY)
};

/** 결제내역 (결제 취소내역 존재 시 같이 포함, 발급된 가상계좌 존재 시 같이 포함) */
export type CtPaymentType = {
  paymentId: string; // 결제내역 id
  billId: string; // 청구 id
  pgcode: string; // 결제수단 코드 (신용카드, 계좌이체, 가상계좌 등)
  orderNo: string; // 거래정 주문번호
  totalAmount: number; // 결제금액
  taxAmount: number; // 부가세 금액(세탁하지 않는 경우 : 결제금액 - 비과세 금액)/11 : 소수점이하 반올림으로 자동 계산)
  productName: string; // 결제 상품(컨텐츠)명
  tid: string; // 결제고유번호
  cid: string; // 승인번호
  payInfo: string; // 결제 부가정보
  transactionDate: string; // 거래일시(YYYY-MM-DD HH:MM:SS)
  installMonth: number; // 할부개월수
  cardInfo: string; // 마스킹(숨김처리) 카드번호 (일반결제(신용카드, 페이코) / 자동결제(신용카드, 페이코) 에만 전달)
  virtualAccount?: CtVirtualAccount; // 가상계좌 정보
  paymentCancel?: CtPaymentCancel; // [Deprecated v1.32] 결제 취소 단건 (누개)
  cancelList?: CtPaymentCancel[]; // 결제취소 내역 리스트
};

/** 결제 취소 정보 */
export type CtPaymentCancel = {
  cancelId: string; // 취소내역 id
  tid: string; // 결제 고유 번호
  cid: string; // 승인번호
  cancelAmount: number; // 취소 금액
  cancelDate: string; // 취소일시(YYYY-MM-DD HH:MM:SS)
};

/** 가상계좌 정보 */
export type CtVirtualAccount = {
  paymentId: string; // 결제내역 id
  accountNo: string; // 가상계좌 번호
  accountName: string; // 가상계좌 입금자명
  accountHolder: string; // 가상계좌 예금주명
  bankCode: string; // 가상계좌 은행 코드
  bankName: string; // 가상계좌 은행명
  issueTid: string; // 가상계좌 채번 승인번호
  expireDate: string; // 가상계좌 채번시 만료일 설정(YYYYMMDD)
  expireTime: string; // 가상계좌 채번시 만료시각 설정(HHMM)
};

/** 수납 정보 */
export type CtReceiptType = {
  receiptId: string; // 수납 ID
  billId: string; // 청구 ID
  billCreditId: string; // 청구 입금 ID
  acctCreditId: string; // 계좌 입금 ID
  paymentId: string; // 결제 ID
  pgcode: PgCode; // 결제수단 (creditcard: 신용카드, banktransfer: 계좌이체, virtualaccount: 가상계좌, physicalcash: (물리적) 현금, other: 기타)
  receiptAmount: number; // 수납 금액
  trDateTime: string; // 거래일시 (수납일시)
  overdueDays: number; // 적용 연체일수
  overdueAmount: number; // 적용 연체금액
  overdueRates: number; // 적용 연체요율
  isDeleted: boolean; // 삭제여부
  createdDate: string; // 생성일
  createdBy: string; // 생성자
  modifiedDate: string; // 수정일
  modifiedBy: string; // 수정자
};

/** 세금계산서 타입 (TAX_NONE: 면세, TAX_ZERO: 영세, TAX_PLUS: 과세) */
export type InvoiceType = "TAX_NONE" | "TAX_ZERO" | "TAX_PLUS";

/** 세금계산서 진행 상태 */
export type TaxInvoiceProgressStatus =
  | "ISSUE_COMPLETE" // (발행) 발행완료
  | "ISSUE_REVOKE" // (발행) 발행취소
  | "TRANS_BEFORE" // (국세청) 전송전
  | "TRANS_READY" // (국세청) 전송대기
  | "TRANS_ONGOING" // (국세청) 전송중
  | "TRANS_SUCCESS" // (국세청) 전송성공
  | "TRANS_FAIL" // (국세청) 전송실패
  | "ISSUE_MANUAL"; // 수기발행

export const TaxInvoiceProgressStatusList = [
  { label: "발행전", value: "", color: "status E" },
  { label: "발행준비", value: "ISSUE_COMPLETE", color: "status E" },
  { label: "요청취소", value: "ISSUE_REVOKE", color: "status G" },
  { label: "발행중", value: "TRANS_BEFORE", color: "status E" },
  { label: "발행중", value: "TRANS_READY", color: "status E" },
  { label: "발행중", value: "TRANS_ONGOING", color: "status E" },
  { label: "발행성공", value: "TRANS_SUCCESS", color: "status F" },
  { label: "발행실패", value: "TRANS_FAIL", color: "status G" },
] as const;

/** 세금계산서 발행 정보 */
export type CtTaxInvoiceType = {
  mgtKey: string; // 관리 ID
  billId: string; // 청구 ID
  ntsConfirmNum: string; // 국세청 승인번호

  /** 정의가 통일되지 않음 - ntsSendDT/ntsSendDt, ntsResultDT/ntsResultDt
   * https://pnpt.atlassian.net/wiki/spaces/MVPD/pages/1386840065/Core+Billing#CtCashbillWebhook
   * https://pnpt.atlassian.net/wiki/spaces/MVPD/pages/26017793/BFF+Contract+Billing#ctTaxInvoice
   */
  ntsSendDT: string; // 국세청 전송일시
  ntsResultDT: string; // 국세청 결과 수신일시

  invoiceType: InvoiceType; // 세금계산서 타입
  progressStatus: TaxInvoiceProgressStatus; // 진행 상태
  prevMgtKey: string; // 이전 발행 ID

  /** 2026.01 memo 없고 invoiceNumber 내려오고 있음 */
  invoiceNumber: string;
  memo?: string; // 기존 invoiceNumber 혹은 임의값 대응을 위해 JsonProperty로 invoiceNumber 지정

  isDeleted: boolean; // 삭제여부
  createdDate: string; // 생성일
  createdBy: string; // 생성자
  modifiedDate: string; // 수정일
  modifiedBy: string; // 수정자
};

/** 현금영수증 발행 정보 */
/** 현금영수증 거래타입 (APPROVE: 승인, REVOKE: 취소) */
export type CashbillTradeType = "APPROVE" | "REVOKE";

/** 현금영수증 거래구분 (INCOME: 소득공제용, EXPENSE: 지출증빙용) */
export type CashbillTradeUsage = "INCOME" | "EXPENSE";

/** 현금영수증 식별타입 */
export type CashbillIdentityType =
  | "RESIDENT_NUM" // 주민등록번호 (소득공제용 한정)
  | "MOBILE_NUM" // 휴대폰 (공통)
  | "CARD_NUM" // 카드번호 (공통)
  | "ISSUE_NUM" // 자진발급용 번호 (소득공제용 한정)
  | "CORP_NUM"; // 사업자번호 (지출증빙용 한정)

/** 현금영수증 진행 상태 */
export type CashbillProgressStatus =
  | "ISSUE_COMPLETE" // (발행) 발행완료
  | "TRANS_SUCCESS" // (국세청) 전송성공
  | "TRANS_FAIL"; // (국세청) 전송실패

export type CtCashbillType = {
  cashbillId: string; // 현금영수증 id
  receiptId: string; // 수납 id
  tradeType: CashbillTradeType; // 거래타입 (APPROVE: 승인, REVOKE: 취소)
  tradeUsage: CashbillTradeUsage; // 거래구분 (INCOME: 소득공제용, EXPENSE: 지출증빙용)
  identityType: CashbillIdentityType; // 식별타입
  identityNum: string; // 식별번호 (현금영수증 조회시 식별타입이 'RESIDENT_NUM' 인 경우, 뒤6자리는 *로 마스킹 처리)
  totalAmount: number; // 거래 총금액 (취소시 경우, 취소할 총금액)
  supplyAmount: number; // 공급가 (취소시 경우, 취소할 공급가)
  taxAmount: number; // 부가세 (취소시 경우, 취소할 부가세)
  corpNum: string; // 발행회원 사업자번호
  mgtKey: string; // 현금영수증 문서번호
  confirmNum: string; // 현금영수증 국세청 승인번호
  tradeDate: string; // 현금영수증 거래일자
  progressStatus: CashbillProgressStatus; // 진행 상태
  prevCashbillId: string; // 이전 현금영수증 id
  isDeleted: boolean; // 삭제 여부
  createdDate: string; // 생성일
  createdBy: string; // 생성자
  modifiedDate: string; // 수정일
  modifiedBy: string; // 수정자
};

/** 청구정보 (정기청구, 연체청구) */
export type CtBillType = {
  billId: string; // 청구 id
  contractId: string; // 계약 id
  providerId: string; // 프로바이더 id
  // 계약자 조직 id (조회 전용). 청구서 생성 시 스케줄의 계약자를 기록하고 발행 후에는 변경되지 않는다.
  // 계약의 계약자(contractManage.mbOrganizationId)와 다를 수 있고, 백필 이전 청구서는 값이 없다.
  organizationId?: string | null;
  supplyType: SupplyType; // 공급유형 (임대, 관리)
  billNumber: string; // 청구번호
  pgcode: PgCode; // 결제방법 코드
  billCustomerName: string; // 청구고객명
  billOrder: number; // 청구회차
  billIssueDate: string; // 청구일자
  billDueDate: string; // 청구기한
  taxInvoiceIssueDate: string; // 세금계산서 발행일
  taxPurposeType: TaxPurposeType; // 세금계산서 발행목적 (청구/영수)
  billStartTime: string; // 이용시작 일시(월별)
  billEndTime: string; // 이용종료 일시(월별)
  baseAmount: string; // 기준가
  adjustAmount: string; // 조정가
  totalAmount: string; // 합계액
  supplyAmount: string; // 공급가
  taxAmount: string; // 부가세
  billDetailList: CtBillDetailType[]; // 청구 상세 리스트 (품목별)
  billPayStatus: BillPayStatus; // 청구결제상태
  generationSource: GenerationSource; // 생성출처
  isOverdueBill: boolean; // 연체청구여부
  isConfirmed: boolean; // 청구서 확정여부 (임대료 청구서 생성시: true, 관리비 청구서 생성시: false)
  parentBillId: string; // 원 청구번호 (연체청구서인 경우, 원청구번호 존재. 없을시 기본값은 '0')
  isDeleted: boolean; // 삭제여부 (false인 값만 전달)
  createdDate: string; // 등록일시
  createdBy: string; // 등록자
  modifiedDate: string; // 수정일시
  modifiedBy: string; // 수정자
  billTitle: string; // 청구제목
  amountDescription: string; // 납부 금액 비고
  billDescription: string; // 산출 금액 비고 (청구서 비고)
};

/** 청구 부가정보 */
export type CtBillEtcType = {
  billId: string; // 청구 id
  receiptCount: number; // 수납 횟수
  taxInvoiceCount: number; // 세금계산서 발행횟수
  chargeEmailSentAt: string; // 청구 공문 발송일시 (마지막 발송건)
};

/** 수납 번들 정보 */
export type CtReceiptBundleType = {
  billTotalAmount: number; // 청구 총금액
  receiptTotalAmount: number; // 수납 총금액
  accruedOverdueAmount: number; // 확정 연체금액
  billUnpaidAmount: number; // 청구 미납금액
  realtimeOverdueAmount: number; // 실시간 연체료
  totalOverdueDays: number; // 총 연체일수
  lastTrDateTime: string; // 마지막 거래일시
  receiptList: CtReceiptType[]; // 수납 리스트
};

/** RCB21. 회차별 청구/연체/결제 목록 조회 - 단일 아이템 */
export type ScheduleRoundNOverdueItem = {
  bill: CtBillType & { scheduleId: number; buildingId: number }; // 청구정보 (정기청구, 연체청구)
  billEtc: CtBillEtcType; // 청구 부가정보
  paymentList: CtPaymentType[]; // 결제내역 리스트
  receiptBundle: CtReceiptBundleType; // 수납 번들 정보
  taxInvoiceList: CtTaxInvoiceType[]; // 세금계산서 발행 목록
  cashbillList: CtCashbillType[]; // 현금영수증 발행 목록
};

/** RCB21. 회차별 청구/연체/결제 목록 조회 Response */
export type ScheduleRoundNOverdueListResponse = {
  content: ScheduleRoundNOverdueItem[];
};

export type AutoPaymentContent = {
  /** 자동 결제 결과 */
  result: boolean;
  /** 페이레터 결제 결과 */
  paymentResult: boolean;
  /** 수납 처리 결과 */
  receiptResult: boolean;
  /** 페이레터 결제 ID */
  paymentId: string;
  /** 수납 정보 */
  receipt: CtReceiptType;
};

/** RCB92. 자동 즉시결제 */
export type AutoPaymentResponse = {
  content: AutoPaymentContent;
};

export { generationSourceLabel, generationSourceList, generationSourceOption, searchStatusOption, searchTypeOption };
export type { ApplicationName, BillPayStep };
